import React, { useEffect, useState } from 'react';
import { loader } from 'graphql.macro';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import QuotesInformation from './QuotesInformation';
import { GET_QUOTES_INFORMATIONVariables } from './graphql/__generated__/GET_QUOTES_INFORMATION';
import AlertLoader from '../../components/gpaCancelPolicy/AlertLoader';
import { WarningText } from '../../components/gpaCancelPolicy/RefundMethodsStyles';
import AlertModal from '../../components/gpaCancelPolicy/InfoAlertModal';
import b64toBlobURL from '../../util/createBase64BlobURL';
import {
  GET_QUOTE_DOCUMENTS,
  GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList,
  GET_QUOTE_DOCUMENTSVariables
} from './graphql/__generated__/GET_QUOTE_DOCUMENTS';
import useFeatureFlag from '../../util/hooks/useFeatureFlag';

const GET_QUOTES_INFORMATION = loader(
  './graphql/Get_Quotes_Information.graphql'
);

const withdraw_quote_by_job = loader(
  './graphql/Withdraw_Quote_By_JobNumber.graphql'
);

const get_quotes_info_location = loader(
  './graphql/Get_Quotes_Info_Location.graphql'
);
const get_quote_documents = loader('./graphql/Get_Quote_Documents.graphql');
const get_document_by_docid = loader('./graphql/Get_Document_By_DocId.graphql');
const get_upload_token = loader('../../components/gpaCancelPolicy/graphql/Get_Upload_Token.graphql');
const upload_quote_document_mutation = loader(
  './graphql/Upload_Quote_Document.graphql'
);
const remove_quote_document = loader('./graphql/Remove_Quote_Document.graphql');
interface QuotesInformationProps {
  quoteId: string;
  continueQuote: boolean;
  sourceSystem: string;
}

const generateRedirect = (connect_url: string, gpa_url: string) => {
  return `${connect_url}/redirect/links?url=${encodeURIComponent(gpa_url)}&sys=detail_GPA`;
};

export default ({
  quoteId,
  continueQuote,
  sourceSystem
}: QuotesInformationProps) => {

  const { flagDetails: redirectToGpa } = useFeatureFlag('RedirectToGPA');
  if (redirectToGpa?.enabled) {
    const isProd = process.env.NODE_ENV === "production";
    let redirectionURL = "";
    if (isProd) {
        const connectURL = "https://saconnect.stateauto.com";
        const baseUrl = "https://std-gpa.stateauto.com";
        const fullPath = `${baseUrl}/gpa/html/index.html%23/quotes/${sourceSystem}/${quoteId}/summary`;
        redirectionURL = generateRedirect(
            connectURL,
            fullPath
        );
    } else {
        const subEnv = sessionStorage.getItem("nonprod_env") || "l3-5";
        const gpaBaseURL = "https://std-gpa#env#.stateauto.com";
        const connectBaseURL = "https://saconnect#env#.stateauto.com";
        const gpaURL = `${gpaBaseURL.replace("#env#", "-" + subEnv)}`;
        const fullPath = `${gpaURL}/gpa/html/index.html%23/quotes/${sourceSystem}/${quoteId}/summary`;
        const connectURL = `${connectBaseURL.replace("#env#", "-" + subEnv)}`;
        redirectionURL = generateRedirect(
            connectURL,
            fullPath
        );
    }
    console.log(`[GPA Redirect] -> ${redirectionURL}`);
    window.location.href = redirectionURL;
  }
  const [redirectLinkError, setRedirectLinkError] = useState<boolean>(false);
  const [quoteDocuments, setQuoteDocuments] = useState<
    (GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList | null)[] | null
  >([]);
  const [filteredAgentDocuments, setFilteredAgentDocuments] = useState<
    (GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList | null)[] | null
  >([]);
  const [documents, setDocuments] = useState<
    (GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList | null)[] | null
  >([]);
  const [lastDeletedDocId, setLastDeletedDocId] = useState<string | null>(null);

  const getUploadTokenResult = useQuery(get_upload_token, {
    variables: {
      "source_system_code": sourceSystem === 'commercial' ? 'GWCL' : 'GWPL',
      "getUploadToken": true,
      "query_env": 'l3-5',
    }
  });
  let uploadToken = "";
  const { data: uploadTokenResponse } = getUploadTokenResult;
  if (uploadTokenResponse) {
    uploadToken = uploadTokenResponse?.generateUploadToken?.data;
  }
  const [filteredPolicyDocuments, setFilteredPolicyDocuments] = useState<
    (GET_QUOTE_DOCUMENTS_getQuotesInformation_documentsList | null)[] | null
  >([]);

  const { loading, error, data } = useQuery(GET_QUOTES_INFORMATION, {
    variables: {
      quoteId,
      continueQuote,
      sourceSystem
    } as GET_QUOTES_INFORMATIONVariables
  });


  const { data: loadQuoteDocsData } = useQuery(get_quote_documents, {
    variables: {
      quoteId,
      getQuoteDocuments: true,
      sourceSystem
    } as GET_QUOTE_DOCUMENTSVariables,
    onCompleted: (data: GET_QUOTE_DOCUMENTS) => {
      if (
        data?.getQuotesInformation?.documentsList &&
        data?.getQuotesInformation?.documentsList.length > 0
      ) {
        const systemGenerated = new Set(['Integration User', 'System User']);
        const documentsArray = data?.getQuotesInformation?.documentsList;
        const agentDocuments = documentsArray.filter(doc => doc && doc?.author && !systemGenerated.has(doc?.author));
        const policyDocuments = documentsArray.filter(doc => doc && (doc?.author ? systemGenerated.has(doc?.author) : !doc?.author));
        setQuoteDocuments(documentsArray);
        setFilteredAgentDocuments(agentDocuments);
        setFilteredPolicyDocuments(policyDocuments);
      }
    },
    onError: error => {
      console.log('Error Reason: ', error.message);
    }
  });

  const [redirectOnQuoteClickAction, redirectOnQuoteClickData] = useLazyQuery(
    get_quotes_info_location,
    {
      fetchPolicy: 'network-only',
      onCompleted: data => {
        let redirectLink = data?.getQuotesInformation?.location;
        if (data && redirectLink) {
          window.location.href = redirectLink;
          setRedirectLinkError(false);
        } else {
          setRedirectLinkError(true);
        }
      },
      onError: error => {
        if (error.message.includes('Cannot take action')) {
          let errorText = error?.message?.split('\n')[1].trim();
          console.log('Error Reason: ', errorText);
          setRedirectLinkError(true);
        }
      }
    }
  );

  const download = (contents: string) => {
    setTimeout(() => {
      const newWindow = window.open('about:blank');
      const newTab =
        newWindow &&
        newWindow.document.body.appendChild(
          newWindow.document.createElement('iframe')
        );
      if (newTab) {
        newTab.width = '100%';
        newTab.height = '100%';
        newTab.setAttribute('style', 'border: 0');
        newTab.src = contents;
      }
    }, 0);
  };

  const [downloadDocument] = useLazyQuery(get_document_by_docid, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const documentData = data?.getQuotesInformation;
      let source;
      let fileType = 'htm';
      fileType = documentData.filetype;
      switch (fileType) {
        case 'pdf':
          source = b64toBlobURL(documentData.contents, 'application/pdf');
          download(source);
          break;
        default:
          throw new Error(`Invalid file type: ${fileType}`);
      }
    },
    onError: error => {
      console.log('Error Reason: ', error.message);
    }
  });

  const { loading: linkLoading } = redirectOnQuoteClickData;

  const [quoteWithdrawByJob, quoteWithdrawByJobData] = useLazyQuery(
    withdraw_quote_by_job,
    {
      onCompleted: data => {
        window.location.reload();
        //  queryResult.refetch();
      },
      onError: error => {
        if (error.message.includes('Cannot take action')) {
          let errorText = error?.message?.split('\n')[1].trim();
          console.log('Error Reason: ', errorText);
          setRedirectLinkError(true);
        }
      }
    }
  );

  const [uploadDoc, { error: docError }] = useMutation(upload_quote_document_mutation);

  const { loading: withdrawLoading } = quoteWithdrawByJobData;

  const withdrawClickHandler = async (quote: string, code: string) => {
    try {
      const env = sessionStorage.getItem('nonprod_env') || 'l3-5';
      quoteWithdrawByJob({
        variables: {
          account_number: '',
          isQuoteWithdraw: true,
          jobNumber: quote,
          queryEnv: env,
          source_system_code: code
        }
      });
    } catch (error) {
      console.error('Withdraw failed:', error);
    }
  };

  const [deleteQuoteDocument] = useLazyQuery(remove_quote_document, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data?.getQuotesInformation?.documentDeleted === true) {
        setDocuments(prev => (prev ?? []).filter(doc => doc?.publicID !== lastDeletedDocId));
      }
      console.log('Document deleted: ', data);
    },
    onError: error => {
      console.log('Error Reason: ', error.message);
    }
  });

  const handleDeleteDoc = (docId: string, sourceSystemCode: string) => {
    setLastDeletedDocId(docId);
    deleteQuoteDocument({
      variables: {
        documentID: docId,
        sourceSystem: sourceSystemCode,
        deleteDoc: true
      }
    });
  };

  let buttonDisabled = linkLoading || withdrawLoading;

  useEffect(() => {
    if (
      loadQuoteDocsData?.getQuotesInformation?.documentsList &&
      loadQuoteDocsData?.getQuotesInformation?.documentsList.length > 0
    ) {
      const systemGenerated = new Set(['Integration User', 'System User']);
      const documentsArray = loadQuoteDocsData?.getQuotesInformation?.documentsList;
      const agentDocuments = documentsArray?.filter((doc: any) => doc && doc?.author && !systemGenerated.has(doc?.author));
      setDocuments(agentDocuments);
    }
  }, [loadQuoteDocsData]);

  if (loading) return <AlertLoader message="Getting Quotes Information" />;
  if (!!redirectLinkError)
    return (
      <AlertModal
        errorMessage="Could not complete your request at this moment. Please try again in sometime."
        setError={setRedirectLinkError}
      />
    );

  if (error || data?.getQuotesInformation?.statusCode === '500')
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <WarningText>
          {' '}
          There is no Quote data available at this moment, please try again
          later.
        </WarningText>
      </div>
    );
  if (!data?.getQuotesInformation) return null;

  return (
    <QuotesInformation
      result={data?.getQuotesInformation}
      redirectOnQuoteClickAction={redirectOnQuoteClickAction}
      sourceSystemCode={sourceSystem}
      withdrawClickHandler={withdrawClickHandler}
      buttonDisabled={buttonDisabled}
      filteredAgentDocuments={documents}
      filteredPolicyDocuments={filteredPolicyDocuments}
      downloadDocument={downloadDocument}
      uploadToken={uploadToken}
      uploadDoc={uploadDoc}
      deleteQuoteDocument={deleteQuoteDocument}
      onDelete={handleDeleteDoc}
    />
  );
};
